// Automation Rules page — /automation (read-only card grid with active toggle).

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";

interface AutomationRule {
  _id: string;
  rule_name: string;
  trigger: { type: string };
  actions: Array<{ type: string }>;
  is_active: boolean;
  created_at: string;
}

interface AutomationResponse { data: AutomationRule[]; total: number; page: number; limit: number; }

function Badge({ label, variant }: { label: string; variant: "gray" | "teal" }): JSX.Element {
  const cls =
    variant === "teal"
      ? "bg-navy-50 text-navy-800 border border-navy-200"
      : "bg-slate-100 text-slate-600 border border-slate-200";
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
      {label.replace(/_/g, " ")}
    </span>
  );
}

function ActiveToggle({ rule }: { rule: AutomationRule }): JSX.Element {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      apiFetch(`/automation/rules/${rule._id}/toggle`, { method: "PATCH" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["automation-rules"] }); },
  });

  return (
    <button
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      className={`text-xs px-3 py-1 rounded-full font-medium transition-colors disabled:opacity-50 ${
        rule.is_active
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
          : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
      }`}
    >
      {rule.is_active ? "Active" : "Inactive"}
    </button>
  );
}

function RuleCard({ rule }: { rule: AutomationRule }): JSX.Element {
  const primaryActionType = rule.actions[0]?.type ?? "no_action";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-slate-900 text-sm leading-snug">{rule.rule_name}</p>
        <ActiveToggle rule={rule} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge label={rule.trigger.type} variant="gray" />
        <Badge label={primaryActionType} variant="teal" />
      </div>
      <p className="text-xs text-slate-400">
        Created {new Date(rule.created_at).toLocaleDateString()}
      </p>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="col-span-2 py-16 text-center">
      <p className="text-sm text-slate-400">No automation rules configured.</p>
    </div>
  );
}

export function AutomationPage(): JSX.Element {
  const { data, isLoading, isError } = useQuery<AutomationResponse>({
    queryKey: ["automation-rules"],
    queryFn: () => apiFetch<AutomationResponse>("/automation/rules"),
  });

  const items = data?.data ?? [];

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">Automation Rules</h1>
        </div>

        {isLoading && (
          <p className="py-8 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>
        )}
        {isError && (
          <p role="alert" className="py-4 text-center text-sm text-red-600">Failed to load automation rules.</p>
        )}
        {!isLoading && !isError && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.length === 0 ? <EmptyState /> : items.map((r) => <RuleCard key={r._id} rule={r} />)}
          </div>
        )}
      </div>
    </Shell>
  );
}
